import { SNIPPET_CLOSE, SNIPPET_OPEN } from '../shared/types'

type Attrs = Record<string, string | number | boolean | EventListener | undefined>

/** Tiny element builder. `on*` keys become listeners, everything else an attribute. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (Node | string | null | undefined)[] = [],
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag)
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue
    if (key.startsWith('on') && typeof value === 'function') {
      element.addEventListener(key.slice(2).toLowerCase(), value as EventListener)
    } else if (value === true) {
      element.setAttribute(key, '')
    } else {
      element.setAttribute(key, String(value))
    }
  }
  for (const child of children) {
    if (child === null || child === undefined) continue
    element.append(typeof child === 'string' ? document.createTextNode(child) : child)
  }
  return element
}

export function select(
  attrs: Attrs,
  options: { value: string; label: string }[],
  selected: string,
): HTMLDivElement {
  const element = h(
    'select',
    { class: 'control', ...attrs },
    options.map((option) =>
      h('option', { value: option.value, selected: option.value === selected }, [option.label]),
    ),
  )
  return h('div', { class: 'field' }, [element])
}

export function toast(message: string): void {
  let host = document.querySelector('.toasts')
  if (!host) {
    host = h('div', { class: 'toasts' })
    document.body.append(host)
  }
  const node = h('div', { class: 'toast' }, [message])
  host.append(node)
  setTimeout(() => node.remove(), 2200)
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Clipboard API needs a secure context and a user gesture; fall back.
    const scratch = h('textarea', { style: 'position:fixed;opacity:0;pointer-events:none' })
    scratch.value = text
    document.body.append(scratch)
    scratch.select()
    const ok = document.execCommand('copy')
    scratch.remove()
    return ok
  }
}

/**
 * A search snippet is a slice of someone's pasta with the matched terms fenced
 * off by control characters. Split on those and hand back text nodes plus
 * `<mark>` elements — the content never becomes markup on the way through.
 */
export function snippetNodes(snippet: string): (HTMLElement | string)[] {
  const nodes: (HTMLElement | string)[] = []
  // Snippets straddle line breaks; they're shown on one line.
  for (const chunk of snippet.replace(/\s+/g, ' ').split(SNIPPET_OPEN)) {
    const close = chunk.indexOf(SNIPPET_CLOSE)
    if (close === -1) {
      if (chunk) nodes.push(chunk)
      continue
    }
    nodes.push(h('mark', {}, [chunk.slice(0, close)]))
    const trailing = chunk.slice(close + SNIPPET_CLOSE.length)
    if (trailing) nodes.push(trailing)
  }
  return nodes
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function byteLength(text: string): number {
  return new TextEncoder().encode(text).byteLength
}

export function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

const UNITS: [limit: number, seconds: number, name: string][] = [
  [60, 1, 'second'],
  [3600, 60, 'minute'],
  [86400, 3600, 'hour'],
  [604800, 86400, 'day'],
  [Number.POSITIVE_INFINITY, 604800, 'week'],
]

/** "3 minutes ago", "just now" */
export function formatRelative(timestamp: number): string {
  const elapsed = Math.max(0, Math.round((Date.now() - timestamp) / 1000))
  if (elapsed < 5) return 'just now'
  for (const [limit, seconds, name] of UNITS) {
    if (elapsed < limit) return `${plural(Math.floor(elapsed / seconds), name)} ago`
  }
  return 'a while ago'
}

/** "expires in 59m 12s" — short units, because it lives in a status bar. */
export function formatCountdown(expiresAt: number): string {
  const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
  if (remaining === 0) return 'expired'
  const days = Math.floor(remaining / 86400)
  const hours = Math.floor((remaining % 86400) / 3600)
  const minutes = Math.floor((remaining % 3600) / 60)
  const seconds = remaining % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}
